import { X, Shield, FileText, CheckCircle, ExternalLink, Calendar, Briefcase } from 'lucide-react';
import { CustomerProductDetailDto } from '../types/dtos';
import { resolveUrl } from '../utils/urlUtils';


interface Props {
    product: CustomerProductDetailDto;
    onClose: () => void;
}

export default function CustomerProductDetailsModal({ product, onClose }: Props) {
    let details: any = {};
    try {
        if (product.detailsJson) {
            details = JSON.parse(product.detailsJson);
        }
    } catch (e) {
        console.error("Failed to parse detailsJson", e);
    }

    const questionAnswers = details.questionAnswers || {};
    const documents = details.documents || {};


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden relative max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="p-6 bg-blue-900 text-white flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold">{product.name}</h2>
                        <p className="text-blue-200 text-xs font-mono">{product.instanceId}</p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8">

                    {/* Policy Summary */}
                    <section>
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            Summary
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase font-bold">Status</label>
                                <div className="mt-1">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${product.status === 'Active' ? 'bg-green-100 text-green-700' :
                                        product.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {product.status}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase font-bold">Premium</label>
                                <p className="font-bold text-blue-900">MK {(product.monthlyPremium || product.onceOffPremium || 0).toLocaleString()}</p>
                                <p className="text-[10px] text-gray-400 capitalize">{product.premiumFrequency || 'Monthly'}</p>
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase font-bold">Plan/Tier</label>
                                <p className="font-bold text-gray-900 text-sm">{product.selectedTierName || 'Standard'}</p>
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase font-bold">Purchase Date</label>
                                <p className="text-sm text-gray-700 font-medium">{new Date(product.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase font-bold">Expiry Date</label>
                                <p className="text-sm text-gray-700 font-medium">{product.expiryDate ? new Date(product.expiryDate).toLocaleDateString() : 'No Expiry'}</p>
                            </div>
                        </div>
                    </section>

                    {/* Dynamic Questions */}
                    <section>
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
                            Application Responses
                        </h3>
                        {Object.keys(questionAnswers).length > 0 ? (
                            <div className="space-y-4">
                                {Object.entries(questionAnswers).map(([key, value]: [string, any]) => (
                                    <div key={key} className="border-b border-gray-100 pb-3">
                                        <label className="text-[10px] text-gray-400 uppercase font-bold mb-1 block">
                                            {key.replace(/_/g, ' ')}
                                        </label>
                                        <div className="text-sm text-gray-900 font-medium">
                                            {Array.isArray(value) ? (
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {value.map((v, i) => (
                                                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs border border-blue-100">
                                                            {v}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : typeof value === 'boolean' ? (
                                                <span className={`flex items-center gap-1 ${value ? 'text-green-600' : 'text-red-500'}`}>
                                                    <CheckCircle size={14} /> {value ? 'Yes / Confirmed' : 'No / Denied'}
                                                </span>
                                            ) : (
                                                String(value)
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 italic bg-gray-50 p-4 rounded-xl text-center border border-dashed border-gray-200">
                                No dynamic information captured for this application.
                            </p>
                        )}
                    </section>

                    {/* Uploaded Documents */}
                    {Object.keys(documents).length > 0 && (
                        <section>
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                Attached Documents
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.entries(documents).map(([key, docUrl]: [string, any]) => {
                                    // docUrl is now a string URL from the backend
                                    const url = resolveUrl(typeof docUrl === 'string' ? docUrl : (docUrl as any).url || (docUrl as any).base64);
                                    const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i) || url.startsWith('data:image');
                                    
                                    // Parse onboarding config to find the human-readable label for this doc key
                                    let label = key;
                                    try {
                                        if (product.onboardingConfigurationJson) {
                                            const config = JSON.parse(product.onboardingConfigurationJson);
                                            const docDef = config.requiredDocuments?.find((d: any) => d.id === key || d.name === key || key.startsWith(d.id));
                                            if (docDef) label = docDef.name;
                                        }
                                    } catch (e) {}

                                    return (
                                        <div key={key} className="group p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition-all flex items-center justify-between shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-white rounded-lg border border-gray-100 text-blue-600 shadow-sm leading-none">
                                                    {isImage ? <Shield size={20} /> : <FileText size={20} />}
                                                </div>
                                                <div className="max-w-[140px]">
                                                    <div className="text-xs font-bold text-gray-900 truncate" title={label}>{label}</div>
                                                    <div className="text-[10px] text-gray-400 uppercase font-mono truncate">{key}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] font-bold text-blue-700 hover:bg-blue-50 transition-colors shadow-sm"
                                                    title="View or Download"
                                                >
                                                    <ExternalLink size={14} />
                                                    DOWNLOAD
                                                </a>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                </div>

                <div className="p-4 bg-gray-50 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-bold transition-colors"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}
